import { Button, Checkbox, Col, Form, Input, Row, Select, Spin } from "antd"
import type { FormInstance } from "antd/lib/form"
import Fieldset from "components/Fieldset"
import { UniversalAddress } from "components/UniversalAddress"
import { IDisplayCompany } from "platform-client/types"
import React from "react"
import { useQuery } from "react-query"
import usePlatform from "contexts/platform/use-platform"
import styled from "styled-components"

export interface ICreateBrokerForm {
  emailAddress?: string
  enabledProducts?: string[]
  address?: {
    line1?: string
    line2?: string
    line3?: string
    locality?: string
    postalCode?: string
    province?: string
    country?: string
    provinceReferenceID?: string
    uniqueReference?: string
  }
  firstName?: string
  lastName?: string
  name: string
  phoneNumber?: string
  title?: string
  abn?: string
  afsl?: string
  status?: "Enabled" | "Disabled"
  parentCompanyID?: string
}

const StyledFieldset = styled(Fieldset)`
  legend {
    color: rgba(0, 0, 0, 0.85);
    font-size: 14px;
  }

  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`

const StyledWidthFormItem = styled(Form.Item)<{ $width?: string }>`
  width: calc(${({ $width }) => $width || "50%"} - 10px);
  margin-left: 5px;
  margin-right: 5px;
  padding-bottom: 10px;
`

export interface IBrokerFormProps {
  form: FormInstance
  onFinish: (values: ICreateBrokerForm) => void
  isLoading?: boolean
  parentCompany?: IDisplayCompany
  parentCompanyDisabled?: boolean
  enabledProductsDisabled?: boolean
  fieldsetTitle?: string
  showCancelButton?: boolean
  onCancel?: () => void
  submitButtonText?: string
  submitButtonLoading?: boolean
}

export const BrokerForm: React.FC<IBrokerFormProps> = ({
  form,
  onFinish,
  isLoading = false,
  parentCompany,
  parentCompanyDisabled = false,
  enabledProductsDisabled = false,
  fieldsetTitle = "Broker details",
  showCancelButton = false,
  onCancel,
  submitButtonText = "Save and continue",
  submitButtonLoading = false
}) => {
  const platform = usePlatform()

  const getProducts = useQuery(
    ["getProducts", "Broker"],
    () => platform.product.getProducts("Broker"),
    {
      enabled: true
    }
  )

  const getCompanies = useQuery(
    ["listCompanies", "Broker"],
    () => platform.company.listCompanies("Broker"),
    {
      enabled: !parentCompanyDisabled
    }
  )

  return (
    <Spin spinning={isLoading || getProducts.isLoading}>
      <Form form={form} onFinish={onFinish} layout="vertical">
        <StyledFieldset title={fieldsetTitle}>
          <StyledWidthFormItem
            name="name"
            label="Name:"
            required
            rules={[
              {
                required: true,
                message: "Please enter the name"
              }
            ]}
          >
            <Input />
          </StyledWidthFormItem>
          <StyledWidthFormItem
            name="enabledProducts"
            label="Enabled products:"
            $width="100%"
          >
            <Checkbox.Group
              style={{ width: "100%" }}
              disabled={enabledProductsDisabled}
            >
              <Row>
                {getProducts.data &&
                  getProducts.data.length > 0 &&
                  getProducts.data.map((item) => (
                    <Col key={item.id} span={12}>
                      <Checkbox value={item.referenceID}>{item.name}</Checkbox>
                    </Col>
                  ))}
              </Row>
            </Checkbox.Group>
          </StyledWidthFormItem>
          <StyledWidthFormItem name="abn" label="ABN:" $width="50%">
            <Input />
          </StyledWidthFormItem>
          <StyledWidthFormItem name="afsl" label="AFSL:" $width="50%">
            <Input />
          </StyledWidthFormItem>
          <StyledWidthFormItem
            name="status"
            label="Status:"
            required
            rules={[
              {
                required: true,
                message: "Please select a status"
              }
            ]}
            $width="50%"
          >
            <Select
              options={[
                { value: "Enabled", label: "Enabled" },
                { value: "Disabled", label: "Disabled" }
              ]}
            />
          </StyledWidthFormItem>
          <StyledWidthFormItem
            name="parentCompanyID"
            label="Parent Company:"
            $width="50%"
          >
            {parentCompanyDisabled && parentCompany ? (
              <Select disabled value={parentCompany.id}>
                <Select.Option value={parentCompany.id || ""}>
                  {parentCompany.name ||
                    parentCompany.referenceID ||
                    parentCompany.id ||
                    ""}
                </Select.Option>
              </Select>
            ) : (
              <Select
                allowClear
                placeholder="Select a parent company (optional)"
                showSearch
                disabled={parentCompanyDisabled}
                filterOption={(input, option) => {
                  const label = option?.label as string | undefined
                  return (label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }}
                options={
                  getCompanies.data?.map((company) => ({
                    value: company.id,
                    label: company.name || company.referenceID || company.id
                  })) || []
                }
              />
            )}
          </StyledWidthFormItem>
        </StyledFieldset>

        <StyledFieldset title="Business Address">
          <StyledWidthFormItem
            name={["address"]}
            label="Business Address"
            rules={[
              {
                validator: (_, v) => {
                  return new Promise((resolve, reject) => {
                    if (v?.line1 && v?.locality && v?.postalCode) {
                      resolve(true)
                    }

                    reject("Business address field is required.")
                  })
                }
              }
            ]}
            $width="100%"
            required
          >
            <UniversalAddress name={"address"}>
              <UniversalAddress.ManualInput />
            </UniversalAddress>
          </StyledWidthFormItem>
        </StyledFieldset>

        <StyledFieldset title="Contact details">
          <StyledWidthFormItem
            name="firstName"
            label="First name:"
            $width="50%"
            required
            rules={[
              {
                required: true,
                message: "Please enter the first name"
              }
            ]}
          >
            <Input />
          </StyledWidthFormItem>
          <StyledWidthFormItem
            name="lastName"
            label="Last name:"
            $width="50%"
            required
            rules={[
              {
                required: true,
                message: "Please enter the last name"
              }
            ]}
          >
            <Input />
          </StyledWidthFormItem>
          <StyledWidthFormItem
            name="phoneNumber"
            label="Telephone:"
            required
            rules={[
              {
                required: true,
                message: "Please enter the telephone number"
              }
            ]}
          >
            <Input type="tel" />
          </StyledWidthFormItem>
          <StyledWidthFormItem
            name="emailAddress"
            label="Email address:"
            required
            rules={[
              {
                required: true,
                message: "Please enter the email address"
              },
              {
                type: "email",
                message: "Please enter a valid email address"
              }
            ]}
          >
            <Input type="email" />
          </StyledWidthFormItem>
        </StyledFieldset>

        <div
          style={{
            marginTop: 20,
            textAlign: showCancelButton ? "right" : "left"
          }}
        >
          {showCancelButton && onCancel && (
            <Button onClick={onCancel} style={{ marginRight: 8 }}>
              Cancel
            </Button>
          )}
          <Button
            {...(showCancelButton ? {} : { size: "large" })}
            type="primary"
            onClick={() => form.submit()}
            loading={submitButtonLoading}
          >
            {submitButtonText}
          </Button>
        </div>
      </Form>
    </Spin>
  )
}
